import { mapWorkspaceMemberRow, workspaceRoleWire } from "../workspaceMemberRow";
import type { WorkspaceMemberRow } from "../workspaceMemberRow";

import { api, request } from "@/lib/api";
import { normalizePlatformRole, PLATFORM_ROLE } from "@/utils/roles";

export type { WorkspaceMemberRow };

type Envelope<T> = { data: T; meta?: PageMeta };
export type PageMeta = {
  page: number;
  limit: number;
  total: number;
  totalPages?: number;
};

export type GovernanceDecision = {
  id: string;
  type: "CAPACITY_EXCEPTION" | "BUDGET_EXCEPTION" | "PHASE_GATE" | "OWNER_ASSIGNMENT" | string;
  workspaceId: string;
  workspaceName: string;
  projectId: string | null;
  projectName: string | null;
  reason: string;
  requestedByUserId: string;
  /** DTO-GAPS-1 (#475): display name for requester — never invent from id. */
  requestedByName?: string;
  requestedAt: string;
  ageHours: number;
  status: "PENDING";
};

export type GovernanceQueueItem = {
  id: string;
  exceptionType: "CAPACITY" | "BUDGET" | "PHASE_GATE" | "OWNER_ASSIGNMENT" | "GOVERNANCE_RULE" | string;
  workspaceId: string;
  workspaceName: string;
  projectId: string | null;
  projectName: string | null;
  reason: string;
  requestedAt: string;
  /** Present on full exception rows from the API (TypeORM). */
  requestedByUserId?: string;
  /** DTO-GAPS-1 (#475): display name for requester (name → email → id). */
  requestedByName?: string | null;
  status: "PENDING" | "APPROVED" | "REJECTED" | "NEEDS_INFO" | "CONSUMED";
  resolvedByUserId?: string | null;
  /** DTO-GAPS-1 (#475): display name for resolver when present. */
  resolvedByName?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** Present when API returns full exception rows (e.g. task BLOCK metadata). */
  metadata?: Record<string, unknown> | null;
  /** Pending age from API — render pending-age UI from this field only. */
  ageHours?: number;
  /**
   * SOD-RENDER-1: true when the resolver IS the requester. This endpoint
   * (`GET /admin/governance/exceptions`) spreads the raw entity, which
   * carries the domain field `selfResolved` (not the `selfApproved` rename
   * `GET /admin/governance/approvals` applies — same underlying flag, two
   * different endpoints, two different field names on the wire). Only
   * meaningful once the row has a resolver — a PENDING row has none yet.
   * Computed authoritatively server-side; never derive client-side.
   */
  selfResolved?: boolean;
};

export type GovernanceHealth = {
  activePolicies: number;
  capacityWarnings: number;
  budgetWarnings: number;
  /**
   * Present only when backend wires a real weekly hard-block count.
   * Hardcoded `0` / missing → do not render a phantom metric (GOV-BUILD WAVE1 Unit 4).
   */
  hardBlocksThisWeek?: number | null;
};

export type GovernanceActivityEvent = {
  id: string;
  timestamp: string | null;
  eventType: string;
  description: string;
  actorUserId: string | null;
  actorName?: string | null;
  exceptionType?: string;
  status?: string;
  workspaceId?: string;
};

/** GET /admin/governance/policies/summary — honest enforcing count (GOV-FIX-B1). */
export type GovernancePolicySummary = {
  workspaceId: string;
  complexityMode: string | null;
  total: number;
  activeCount: number;
  evaluableActiveCount: number;
};

export type WorkspaceOwner = { userId: string; name: string; email: string };
export type WorkspaceSnapshotRow = {
  workspaceId: string;
  workspaceName: string;
  /** Non-deleted projects; null = not read (the un-enriched list endpoint carries none). */
  projectCount: number | null;
  budgetStatus: "OK" | "WARNING" | "BLOCKED" | "UNKNOWN";
  capacityStatus: "OK" | "WARNING" | "BLOCKED" | "UNKNOWN";
  /** PENDING governance exceptions; null = not read / could not be read (never 0). */
  openExceptions: number | null;
  owners: WorkspaceOwner[];
  status: "ACTIVE" | "ARCHIVED";
};

/** Row shape from GET /api/admin/workspaces (AdminController.getWorkspaces). */
export type AdminWorkspaceListRow = {
  id: string;
  name: string;
  owner?: { id: string; email?: string | null; name?: string | null } | null;
  visibility?: string;
  status?: string;
  createdAt?: string;
};

/**
 * Maps admin workspace list API rows into WorkspaceSnapshotRow for Admin UI tables.
 * Governance enrichment (budget/capacity/exceptions/projectCount) is not returned by
 * this endpoint. Sentinel `UNKNOWN` means "not enriched" — render via
 * `formatWorkspaceHealthStatus` / `WorkspaceHealthStatusText`, never as a live status.
 */
export function mapAdminWorkspaceListItemToSnapshotRow(
  raw: AdminWorkspaceListRow,
): WorkspaceSnapshotRow {
  const owner = raw.owner;
  const owners: WorkspaceOwner[] = owner
    ? [
        {
          userId: String(owner.id ?? ""),
          name: (owner.name || owner.email || "Unknown").trim() || "Unknown",
          email: String(owner.email ?? ""),
        },
      ]
    : [];
  const archived = String(raw.status ?? "").toLowerCase() === "archived";
  return {
    workspaceId: String(raw.id ?? ""),
    workspaceName: String(raw.name ?? "Workspace"),
    // The list endpoint carries no project counts: unknown, not zero.
    projectCount: null,
    budgetStatus: "UNKNOWN",
    capacityStatus: "UNKNOWN",
    // The list endpoint carries no exception counts: unknown, not zero.
    openExceptions: null,
    owners,
    status: archived ? "ARCHIVED" : "ACTIVE",
  };
}

export type UserWorkspaceAccess = {
  workspaceId: string;
  workspaceName: string;
  accessLevel: "workspace_owner" | "delivery_owner" | "contributor" | "viewer";
};

export type AdminTeamMember = {
  id: string;
  userId: string;
  role: string;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
};

export type AdminTeamSummary = {
  id: string;
  name: string;
  shortCode: string;
  color?: string | null;
  visibility?: string;
  description?: string | null;
  workspaceId?: string | null;
  status: string;
  memberCount: number;
  projectCount: number;
  createdAt: string;
  updatedAt: string;
};

export type AdminTeamDetail = AdminTeamSummary & {
  members: AdminTeamMember[];
};

export type AdminDirectoryUser = {
  id: string;
  name: string;
  email: string;
  /** UI dropdown value (owner/admin → admin, pm → member, viewer). */
  role: "admin" | "member" | "viewer" | "owner";
  membershipRole?: string;
  platformRole?: "admin" | "member" | "viewer";
  teams: Array<{ id: string; name: string }>;
  status: "active" | "suspended" | "invited";
  workspaceAccess: UserWorkspaceAccess[];
  lastActiveAt?: string | null;
  joinedAt?: string | null;
  isOwner?: boolean;
};

/** Row from GET /api/orgs/:orgId/invites (INVITE-ADMIN-1). */
export type OrgInviteListItem = {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  name: string | null;
  invitedByLabel: string;
  invitedAt: string | null;
};

export type AdminTemplate = {
  id: string;
  name: string;
  status?: "DRAFT" | "APPROVED" | "ARCHIVED";
  updatedAt?: string;
  updatedByUserId?: string | null;
  /** System (Template Center) vs org-owned template — from unified templates list. */
  isSystem?: boolean;
  deliveryMethod?: string | null;
  methodology?: string | null;
};

export type GovernanceRuleCondition = {
  type: string;
  field?: string;
  value?: unknown;
  relatedEntity?: string;
  params?: Record<string, unknown>;
};

export type GovernanceRuleDefinition = {
  when?: { fromStatus?: string; toStatus?: string };
  conditions?: GovernanceRuleCondition[];
  message?: string;
  severity?: string;
  /** Roadmap quarter label from DB (e.g. Q3-2026) — hide enforcement toggle in UI. */
  roadmap?: string;
};

/** Governance policy row for a template (GET/PATCH template governance). */
export interface GovernancePolicyItem {
  code: string;
  name: string;
  entityType: string;
  enforcementMode: string;
  enabled: boolean;
  ruleDefinition: GovernanceRuleDefinition;
  systemRuleSetId: string;
  templateRuleSetId: string | null;
}

/** Org-wide catalog entry — classic endpoint removed in FE-GOV-1; kept for ManageScope modal typing only. */
export interface GovernanceCatalogItem {
  code: string;
  name: string;
  entityType: string;
  enforcementMode: string;
  ruleDefinition: GovernanceRuleDefinition;
  /** @deprecated Never trust — classic catalog count was a lie. */
  activeOnTemplates?: number | null;
}

/** Workspace governance policy row (GET/PUT /admin/governance/policies). */
export type PolicyWhenParam = {
  key: string;
  label: string;
  value: number | string;
  unit: string | null;
  editable: boolean;
  min: number | null;
  max: number | null;
};

export type PolicyState = "ENFORCING" | "DISABLED" | "NOT_EVALUABLE";

export type WorkspaceGovernancePolicy = {
  code: string;
  name: string;
  /** Self-describing catalog label (GOV-FIX-B1). Falls back to name. */
  humanLabel?: string;
  description: string;
  /**
   * Legacy enforcement-object category. Prefer `scope.label` from the Unit 5 contract.
   * @deprecated for display — use sentence-view `scope`
   */
  scope: string | { tier?: string; label?: string };
  /** Runtime event this policy hooks — show when isEvaluable. */
  enforcementPoint?: string;
  /**
   * @deprecated Prefer `verdict` (Unit 5). Kept for backward-compat payloads.
   */
  outcome?: "BLOCK" | "WARN" | null;
  /**
   * @deprecated Prefer `verdict`.
   */
  severityEffective?: "WARN" | "BLOCK" | string | null;
  source: "workspace" | "bundle" | "disabled";
  /** Alias of isEnabled from W2 payload. */
  enabled?: boolean;
  isEnabled: boolean;
  /**
   * Honesty primitive: false when no evaluator/data source exists.
   * UI must never claim enforcement when this is false.
   */
  isEvaluable?: boolean;
  /**
   * @deprecated Prefer `when.params` (Unit 5). Top-level params are legacy.
   */
  params?: Record<string, unknown> | null;
  bundleDefaults?: Record<string, unknown> | null;

  // ── Unit 5 sentence-view contract (prefer these) ─────────────────────────
  when?: {
    text: string | null;
    params: PolicyWhenParam[];
  };
  /** Structured scope — when present, use `.label` for Where. */
  // scope may also be the legacy string; see union above
  verdict?: "ALLOW" | "WARN" | "BLOCK" | null;
  release?: {
    requiredRole: string;
    approvalsRequired: number;
    label: string;
  } | null;
  state?: PolicyState;
  stateReason?: string | null;
};

/** Resolved approval row from GET /admin/governance/approvals (Unit 5.5). */
export type GovernanceApproval = {
  id: string;
  type: string;
  workspaceId: string;
  projectId: string | null;
  reason?: string | null;
  /** APPROVED | REJECTED | CONSUMED */
  decision?: string;
  requestedByUserId?: string;
  requestedByName?: string | null;
  resolvedByUserId?: string | null;
  resolvedByName?: string | null;
  selfApproved?: boolean;
  resolutionNote?: string | null;
  requestedAt: string | null;
  /**
   * Best-effort resolution time (terminal updated_at — no resolved_at column).
   * Do not present as precise.
   */
  decidedAt?: string | null;
  /** Legacy stub fields — ignore when decision/decidedAt present. */
  status?: "PENDING" | "COMPLETED" | string;
  requiredApprovals?: number;
  receivedApprovals?: number;
};

export type AdminAuditEvent = {
  id: string;
  createdAt: string;
  actorUserId: string | null;
  actorName?: string | null;
  action: string;
  entityType?: string;
  entityId?: string;
  description: string;
};

/** Row from GET /admin/trash (paginated). */
export type AdministrationTrashItem = {
  id: string;
  name: string;
  type: "workspace" | "project" | "task";
  displayType?: string;
  location?: string;
  deletedAt: string;
  deletedByUserId?: string | null;
  deletedByName?: string | null;
  workspaceId?: string | null;
  /** TRASH-TRUTH-1: set when this row cannot be permanently deleted. */
  purgeBlockedReason?: string | null;
};

/** Stream A — optional staging migration audit endpoint. */
export type RbacMigrationSummary = {
  migratedUserCount: number;
  pmMappingExceptions: Array<{
    email: string;
    resolution: string;
    notes: string;
  }>;
  /** ISO timestamp from server; used for 7-day auto-hide. */
  generatedAt?: string;
};

// MVP-3A: Workspace member management types

export type OrgMemberOption = {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
};

function unwrapData<T>(payload: T | Envelope<T> | { __zephixInner: T }): T {
  if (payload && typeof payload === "object" && "__zephixInner" in (payload as any)) {
    return (payload as { __zephixInner: T }).__zephixInner;
  }
  if (payload && typeof payload === "object" && "data" in (payload as any)) {
    return (payload as Envelope<T>).data;
  }
  return payload as T;
}

function unwrapMeta<T>(payload: T | Envelope<T> | { __zephixMeta: PageMeta }): PageMeta | null {
  if (payload && typeof payload === "object" && "__zephixMeta" in (payload as any)) {
    return ((payload as { __zephixMeta: PageMeta }).__zephixMeta || null) as PageMeta | null;
  }
  if (payload && typeof payload === "object" && "meta" in (payload as any)) {
    return ((payload as Envelope<T>).meta || null) as PageMeta | null;
  }
  return null;
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** Normalize W2 / Unit 5 policy row: prefer sentence-view fields; keep honesty fields. */
function normalizeWorkspaceGovernancePolicy(
  row: WorkspaceGovernancePolicy,
): WorkspaceGovernancePolicy {
  const isEnabled = row.isEnabled ?? row.enabled ?? false;
  const scopeLabel =
    typeof row.scope === "object" && row.scope !== null && "label" in row.scope
      ? String((row.scope as { label?: string }).label ?? "")
      : typeof row.scope === "string"
        ? row.scope
        : "";
  return {
    ...row,
    isEnabled,
    enabled: row.enabled ?? isEnabled,
    isEvaluable: row.isEvaluable,
    humanLabel: row.humanLabel ?? row.name,
    enforcementPoint: row.enforcementPoint,
    // Do not invent verdict from outcome — sentence view reads verdict only.
    outcome: row.outcome ?? (row.severityEffective as "BLOCK" | "WARN" | null) ?? null,
    scope: row.scope ?? scopeLabel,
  };
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function invitedByLabelFromRaw(raw: Record<string, unknown>): string {
  const named = [raw.invitedByName, raw.createdByName, raw.inviterName];
  for (const value of named) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  const by = raw.invitedBy ?? raw.invited_by ?? raw.creator;
  if (typeof by === "string" && by.trim() && !UUID_RE.test(by.trim())) {
    return by.trim();
  }
  if (by && typeof by === "object") {
    const o = by as { name?: unknown; email?: unknown; fullName?: unknown };
    if (typeof o.name === "string" && o.name.trim()) return o.name.trim();
    if (typeof o.fullName === "string" && o.fullName.trim()) return o.fullName.trim();
    if (typeof o.email === "string" && o.email.trim()) return o.email.trim();
  }
  return "—";
}

function parseOrgInviteList(payload: unknown): OrgInviteListItem[] {
  const inner = unwrapData(payload as { data?: unknown });
  const rows: unknown[] = Array.isArray(inner)
    ? inner
    : asArray(
        inner && typeof inner === "object"
          ? (inner as { invites?: unknown }).invites
          : payload && typeof payload === "object"
            ? (payload as { invites?: unknown }).invites
            : [],
      );
  return rows
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      const raw = row as Record<string, unknown>;
      const id = String(raw.id ?? "");
      const email = String(raw.email ?? "");
      if (!id && !email) return null;
      const createdAt = String(raw.createdAt ?? raw.created_at ?? "");
      const invitedAtRaw = raw.invitedAt ?? raw.invited_at;
      return {
        id,
        email,
        role: String(raw.role ?? ""),
        status: String(raw.status ?? "pending").toLowerCase(),
        expiresAt: String(raw.expiresAt ?? raw.expires_at ?? ""),
        createdAt,
        name: typeof raw.name === "string" && raw.name.trim() ? raw.name.trim() : null,
        invitedByLabel: invitedByLabelFromRaw(raw),
        invitedAt:
          typeof invitedAtRaw === "string" && invitedAtRaw
            ? invitedAtRaw
            : createdAt || null,
      } satisfies OrgInviteListItem;
    })
    .filter((row): row is OrgInviteListItem => row !== null);
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      qs.set(key, String(value));
    }
  });
  return qs.toString() ? `?${qs.toString()}` : "";
}

/** One row of POST /admin/organization/users/invite `results`. */
export type AdminInviteResultRow = {
  email: string;
  status: "success" | "error";
  code?: string;
  message?: string;
  /**
   * EMAIL-TRUTH-1: on a success row that enqueued an email. SUPPRESSED means
   * the staging recipient allow-list will not deliver it.
   */
  delivery?: "QUEUED" | "SUPPRESSED";
  deliveryReason?: "ALLOWLIST_EMPTY" | "NOT_ALLOWLISTED";
};

/**
 * ADMIN-GATES-ATTENTION-1 — GET /admin/governance/gates-attention.
 * Mirrors zephix-backend gate-attention.service.ts.
 */
export type ProjectAtRisk = {
  projectId: string;
  /** Null when the project row can no longer be read. */
  projectName: string | null;
  workspaceId: string | null;
  workspaceName: string | null;
  activeRiskCount: number;
};

export type ProjectsAtRiskList = {
  /** Every project at risk — the figure the tile shows. */
  total: number;
  /** Most active risks first; capped, so `total` can exceed `projects.length`. */
  projects: ProjectAtRisk[];
};

export type GatesAttentionItem = {
  submissionId: string;
  workspaceId: string;
  workspaceName: string | null;
  projectId: string;
  projectName: string | null;
  stepName: string | null;
  submittedAt: string | null;
};

export type GatesAttentionNoEligibleItem = GatesAttentionItem & {
  reason: "NO_CHAIN" | "NO_ACTIVE_STEP" | "NOBODY_QUALIFIES";
  /** The signed-in admin is an active member of the gate's workspace. */
  canAssign: boolean;
};

export type GatesAttention = {
  scanned: number;
  totalSubmitted: number;
  truncated: boolean;
  needsYourDecision: GatesAttentionItem[];
  noEligibleApprover: GatesAttentionNoEligibleItem[];
  awaitingOthersCount: number;
  notEvaluableCount: number;
};

export const administrationApi = {
  async listPendingDecisions(params?: {
    page?: number;
    limit?: number;
    workspaceId?: string;
    projectId?: string;
    type?: string;
  }): Promise<{ data: GovernanceDecision[]; meta: PageMeta | null }> {
    const query = buildQuery(params || {});
    // Raw `api`, not `request`: `request.get` returns the inner array and drops
    // `__zephixMeta`, so `meta.total` never reached the caller and a full page
    // read as "Unavailable" (D-99). The interceptor's own output carries both.
    const payload = await api.get<unknown, Envelope<GovernanceDecision[]>>(
      `/admin/governance/decisions/pending${query}`,
    );
    return { data: asArray(unwrapData(payload)), meta: unwrapMeta(payload) };
  },

  /**
   * `GET /admin/risk/summary` → projects carrying at least one ACTIVE risk.
   * A count, or `{ status: "unavailable" }` — never a stand-in 0. A rejected
   * call is the caller's "Unavailable" too.
   */
  async getProjectsAtRiskCount(): Promise<number | { status: "unavailable" }> {
    const payload = await request.get<Envelope<{ projectsAtRisk: unknown }>>(
      "/admin/risk/summary",
    );
    const value = unwrapData(payload)?.projectsAtRisk;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    return { status: "unavailable" };
  },

  /** `GET /admin/risk/projects` → the projects behind that count. */
  async listProjectsAtRisk(): Promise<ProjectsAtRiskList> {
    const payload = await request.get<Envelope<ProjectsAtRiskList>>(
      "/admin/risk/projects",
    );
    return unwrapData(payload);
  },

  async getGatesAttention(): Promise<GatesAttention> {
    const payload = await request.get<Envelope<GatesAttention>>(
      "/admin/governance/gates-attention",
    );
    return unwrapData(payload);
  },

  async getGovernanceHealth(): Promise<GovernanceHealth> {
    const payload = await request.get<Envelope<GovernanceHealth>>("/admin/governance/health");
    return unwrapData(payload);
  },

  async listGovernanceQueue(params?: {
    page?: number;
    limit?: number;
    workspaceId?: string;
    status?: "PENDING" | "APPROVED" | "REJECTED" | "NEEDS_INFO" | "CONSUMED";
  }): Promise<{ data: GovernanceQueueItem[]; meta: PageMeta | null }> {
    const query = buildQuery(params || {});
    const payload = await request.get<Envelope<GovernanceQueueItem[]>>(
      `/admin/governance/exceptions${query}`,
    );
    return { data: asArray(unwrapData(payload)), meta: unwrapMeta(payload) };
  },

  async listGovernanceApprovals(params?: {
    page?: number;
    limit?: number;
    workspaceId?: string;
  }): Promise<{ data: GovernanceApproval[]; meta: PageMeta | null }> {
    const query = buildQuery(params || {});
    const payload = await request.get<Envelope<GovernanceApproval[]>>(
      `/admin/governance/approvals${query}`,
    );
    return { data: asArray(unwrapData(payload)), meta: unwrapMeta(payload) };
  },

  async approveException(
    id: string,
    resolutionNote?: string | null,
  ): Promise<{ id: string; status: "APPROVED"; updatedAt: string }> {
    const payload = await request.post<Envelope<{ id: string; status: "APPROVED"; updatedAt: string }>>(
      `/admin/governance/exceptions/${id}/approve`,
      { note: resolutionNote?.trim() ? resolutionNote.trim() : undefined },
    );
    return unwrapData(payload);
  },

  async rejectException(id: string, reason: string, comment?: string | null): Promise<{ id: string; status: "REJECTED"; updatedAt: string }> {
    const payload = await request.post<Envelope<{ id: string; status: "REJECTED"; updatedAt: string }>>(
      `/admin/governance/exceptions/${id}/reject`,
      { reason, comment: comment ?? null },
    );
    return unwrapData(payload);
  },

  async requestMoreInfo(id: string, question: string, comment?: string | null): Promise<{ id: string; status: "NEEDS_INFO"; updatedAt: string }> {
    const payload = await request.post<Envelope<{ id: string; status: "NEEDS_INFO"; updatedAt: string }>>(
      `/admin/governance/exceptions/${id}/request-info`,
      { question, comment: comment ?? null },
    );
    return unwrapData(payload);
  },

  async listRecentActivity(opts?: {
    limit?: number;
    workspaceId?: string;
  }): Promise<GovernanceActivityEvent[]> {
    const limit = opts?.limit ?? 20;
    const payload = await request.get<Envelope<GovernanceActivityEvent[]>>(
      `/admin/governance/activity/recent${buildQuery({
        limit,
        workspaceId: opts?.workspaceId,
      })}`,
    );
    return asArray(unwrapData(payload));
  },

  async getWorkspaceSnapshot(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{ data: WorkspaceSnapshotRow[]; meta: PageMeta | null }> {
    const payload = await request.get<Envelope<WorkspaceSnapshotRow[]>>(
      `/admin/workspaces/snapshot${buildQuery(params || {})}`,
    );
    const meta = unwrapMeta(payload);
    const snapshotRows = asArray<WorkspaceSnapshotRow>(unwrapData(payload));
    if (snapshotRows.length > 0) {
      return { data: snapshotRows, meta };
    }
    // Snapshot enrichment can fail server-side while /admin/workspaces still lists rows.
    const fallback = await this.listWorkspaces();
    return {
      data: fallback,
      meta: meta ?? {
        page: params?.page ?? 1,
        limit: params?.limit ?? fallback.length,
        total: fallback.length,
      },
    };
  },

  async listWorkspaces(): Promise<WorkspaceSnapshotRow[]> {
    const payload = await request.get<Envelope<AdminWorkspaceListRow[]>>("/admin/workspaces");
    const rows = asArray<AdminWorkspaceListRow>(unwrapData(payload));
    return rows.map(mapAdminWorkspaceListItemToSnapshotRow);
  },

  async listUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: string;
  }): Promise<{
    data: AdminDirectoryUser[];
    meta: PageMeta | null;
    seatLimit: number | null;
    memberCount: number;
  }> {
    // MVP-2.1 fix: The backend GET /admin/users returns
    // { users: [{id, email, firstName, lastName, role, status, ...}], pagination: {...} }
    // NOT { data: [...] }. The response interceptor passes it through as-is
    // because there's no outer `data` key. We need to:
    //   1. Extract the `users` array from the raw response
    //   2. Transform each user to the AdminDirectoryUser shape (combine
    //      firstName+lastName into `name`, add empty `workspaceAccess`)
    //   3. Extract pagination into the PageMeta format
    const raw = await request.get<any>(`/admin/users${buildQuery(params || {})}`);
    const rawPayload = raw && typeof raw === "object" ? raw : {};
    const usersArr: any[] = Array.isArray(rawPayload.users)
      ? rawPayload.users
      : Array.isArray(rawPayload) // fallback if interceptor unwrapped differently
        ? rawPayload
        : asArray(unwrapData(rawPayload));

    const data: AdminDirectoryUser[] = usersArr.map((u: any) => {
      const ui = String(u.uiRole || "").toLowerCase();
      const prRaw = String(u.platformRole ?? "member");
      const normalizedPlatform = normalizePlatformRole(prRaw);
      const platformOk: NonNullable<AdminDirectoryUser["platformRole"]> =
        normalizedPlatform === PLATFORM_ROLE.ADMIN
          ? "admin"
          : normalizedPlatform === PLATFORM_ROLE.VIEWER
            ? "viewer"
            : "member";
      const roleForRow: AdminDirectoryUser["role"] =
        ui === "owner"
          ? "owner"
          : normalizedPlatform === PLATFORM_ROLE.ADMIN
            ? "admin"
            : normalizedPlatform === PLATFORM_ROLE.VIEWER
              ? "viewer"
              : "member";
      return {
        id: String(u.id ?? ""),
        name: [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email || "Unknown",
        email: u.email ?? "",
        role: roleForRow,
        membershipRole: u.membershipRole,
        platformRole: platformOk,
        teams: Array.isArray(u.teams) ? u.teams : [],
        status: (u.status ?? "active") as AdminDirectoryUser["status"],
        workspaceAccess: Array.isArray(u.workspaceAccess) ? u.workspaceAccess : [],
        lastActiveAt: u.lastActive ?? null,
        joinedAt: u.joinedAt ?? null,
        isOwner: Boolean(u.isOwner),
      };
    });

    const pag = rawPayload.pagination;
    const meta: PageMeta | null = pag
      ? {
          page: pag.page ?? 1,
          limit: pag.limit ?? 20,
          total: pag.total ?? data.length,
          totalPages: pag.totalPages,
        }
      : null;

    const seatRaw = rawPayload.seatLimit;
    const seatLimit =
      seatRaw !== undefined && seatRaw !== null && Number.isFinite(Number(seatRaw))
        ? Number(seatRaw)
        : null;
    const memberCount =
      typeof rawPayload.memberCount === "number" ? rawPayload.memberCount : meta?.total ?? data.length;

    return { data, meta, seatLimit, memberCount };
  },

  async getOrgTemplate(templateId: string): Promise<Record<string, unknown>> {
    return request.get(`/admin/templates/${templateId}`);
  },

  async patchOrgTemplate(
    templateId: string,
    patch: { columnConfig?: Record<string, boolean | string | string[]> },
  ): Promise<Record<string, unknown>> {
    return request.patch(`/admin/templates/${templateId}`, patch);
  },

  async listAdminTeams(params?: {
    search?: string;
    page?: number;
    limit?: number;
    status?: string;
    workspaceId?: string;
  }): Promise<AdminTeamSummary[]> {
    const rows = await request.get<AdminTeamSummary[]>(`/admin/teams${buildQuery(params || {})}`);
    return asArray(rows);
  },

  async getAdminTeam(teamId: string): Promise<AdminTeamDetail> {
    return request.get<AdminTeamDetail>(`/admin/teams/${teamId}`);
  },

  async createAdminTeam(body: {
    name: string;
    shortCode: string;
    description?: string;
    visibility?: string;
    color?: string;
    workspaceId?: string;
  }): Promise<AdminTeamSummary> {
    return request.post<AdminTeamSummary>(`/admin/teams`, {
      ...body,
      visibility: body.visibility ?? "public",
    });
  },

  async updateAdminTeam(
    teamId: string,
    body: Partial<{
      name: string;
      shortCode: string;
      description: string;
      visibility: string;
      color: string;
      workspaceId: string;
      status: string;
    }>,
  ): Promise<AdminTeamSummary> {
    return request.patch<AdminTeamSummary>(`/admin/teams/${teamId}`, body);
  },

  async deleteAdminTeam(teamId: string): Promise<AdminTeamSummary> {
    return request.delete<AdminTeamSummary>(`/admin/teams/${teamId}`);
  },

  async addTeamMember(teamId: string, userId: string): Promise<AdminTeamDetail> {
    return request.post<AdminTeamDetail>(`/admin/teams/${teamId}/members`, { userId });
  },

  async removeTeamMember(teamId: string, userId: string): Promise<AdminTeamDetail> {
    return request.delete<AdminTeamDetail>(`/admin/teams/${teamId}/members/${userId}`);
  },

  async changeUserRole(
    userId: string,
    role: "admin" | "member" | "viewer",
  ): Promise<{ userId: string; role: "admin" | "member" | "viewer"; updatedAt: string }> {
    const orgRole = role === "admin" ? "ADMIN" : role === "viewer" ? "VIEWER" : "MEMBER";
    await request.patch(`/v1/org/users/${encodeURIComponent(userId)}`, { orgRole });
    return { userId, role, updatedAt: new Date().toISOString() };
  },

  async deactivateUser(
    userId: string,
    reason?: string | null,
  ): Promise<void> {
    void reason;
    await request.patch(`/v1/org/users/${encodeURIComponent(userId)}/deactivate`, {});
  },

  /**
   * AUTH-1F — POST /api/admin/users/:userId/reset-link
   * Admin-only, org-scoped. Returns a one-time 1-hour reset URL for manual handoff.
   */
  async generateUserResetLink(userId: string): Promise<{
    resetLink: string;
    expiresAt: string;
    userId: string;
    expiresInMinutes: number;
  }> {
    const raw = await request.post<{
      resetLink: string;
      expiresAt: string | Date;
      userId: string;
      expiresInMinutes?: number;
    }>(`/admin/users/${encodeURIComponent(userId)}/reset-link`, {});
    return {
      resetLink: String(raw.resetLink ?? ""),
      expiresAt:
        typeof raw.expiresAt === "string"
          ? raw.expiresAt
          : new Date(raw.expiresAt).toISOString(),
      userId: String(raw.userId ?? userId),
      expiresInMinutes:
        typeof raw.expiresInMinutes === "number" ? raw.expiresInMinutes : 60,
    };
  },

  /**
   * Build 1 — POST /api/v1/org/users/invite (single invite).
   */
  async inviteOrgUserV1(body: {
    email: string;
    fullName?: string;
    orgRole: "ADMIN" | "MEMBER" | "VIEWER";
    workspaceAssignments?: Array<{ workspaceId: string; accessLevel: string }>;
  }): Promise<{ invitationId: string; email: string; expiresAt: string }> {
    return request.post(`/v1/org/users/invite`, body);
  },

  /**
   * GET /api/orgs/:orgId/invites — pending by default on the server.
   * Tokens are never returned. Optional display fields (name, invitedBy) are
   * mapped when present; missing fields render as "—" in the UI.
   */
  async listOrgInvites(orgId: string): Promise<OrgInviteListItem[]> {
    if (!orgId) return [];
    const raw = await request.get<unknown>(`/orgs/${encodeURIComponent(orgId)}/invites`);
    return parseOrgInviteList(raw);
  },

  /** POST /api/orgs/:orgId/invites/:inviteId/resend */
  async resendOrgInvite(orgId: string, inviteId: string): Promise<void> {
    await request.post(
      `/orgs/${encodeURIComponent(orgId)}/invites/${encodeURIComponent(inviteId)}/resend`,
      {},
    );
  },

  /** DELETE /api/orgs/:orgId/invites/:inviteId */
  async revokeOrgInvite(orgId: string, inviteId: string): Promise<void> {
    await request.delete(
      `/orgs/${encodeURIComponent(orgId)}/invites/${encodeURIComponent(inviteId)}`,
    );
  },

  async inviteUsers(input: {
    emails: string[];
    /**
     * AdminInviteDto accepts Admin | Member | Guest.
     * Viewer is accepted here as a UI alias and mapped to Guest on the wire.
     * Admin is sent as Admin: it must never be coerced to Member.
     */
    platformRole: "Admin" | "Member" | "Guest" | "Viewer";
    workspaceAssignments?: Array<{
      workspaceId: string;
      accessLevel: "Member" | "Guest" | "Viewer";
    }>;
  }): Promise<{ results: AdminInviteResultRow[] }> {
    const toAccessLevel = (r: "Member" | "Guest" | "Viewer"): "Member" | "Guest" =>
      r === "Viewer" || r === "Guest" ? "Guest" : "Member";
    const toPlatformRole = (
      r: "Admin" | "Member" | "Guest" | "Viewer",
    ): "Admin" | "Member" | "Guest" => (r === "Admin" ? "Admin" : toAccessLevel(r));
    const body = {
      emails: input.emails,
      platformRole: toPlatformRole(input.platformRole),
      workspaceAssignments: input.workspaceAssignments?.map((a) => ({
        workspaceId: a.workspaceId,
        accessLevel: toAccessLevel(a.accessLevel),
      })),
    };
    const payload = await request.post<
      Envelope<{ results: AdminInviteResultRow[] }>
    >("/admin/organization/users/invite", body);
    return unwrapData(payload);
  },

  async listTemplates(): Promise<AdminTemplate[]> {
    const payload = await request.get<Envelope<AdminTemplate[]>>("/admin/templates");
    return asArray(unwrapData(payload));
  },

  async getTemplateGovernance(templateId: string): Promise<GovernancePolicyItem[]> {
    const payload = await request.get<Envelope<GovernancePolicyItem[]>>(
      `/admin/templates/${templateId}/governance`,
    );
    return asArray(unwrapData(payload));
  },

  async updateTemplateGovernance(
    templateId: string,
    toggles: Record<string, boolean>,
  ): Promise<GovernancePolicyItem[]> {
    const payload = await request.patch<Envelope<GovernancePolicyItem[]>>(
      `/admin/templates/${templateId}/governance`,
      toggles,
    );
    return asArray(unwrapData(payload));
  },

  async getGovernancePolicySummary(
    workspaceId: string,
  ): Promise<GovernancePolicySummary> {
    const payload = await request.get<
      Envelope<GovernancePolicySummary> | GovernancePolicySummary
    >(`/admin/governance/policies/summary${buildQuery({ workspaceId })}`);
    return unwrapData(payload) as GovernancePolicySummary;
  },

  async listWorkspaceGovernancePolicies(
    workspaceId: string,
  ): Promise<WorkspaceGovernancePolicy[]> {
    const payload = await request.get<
      Envelope<{ policies: WorkspaceGovernancePolicy[] }> | { policies: WorkspaceGovernancePolicy[] }
    >(`/admin/governance/policies${buildQuery({ workspaceId })}`);
    const data = unwrapData(payload);
    if (data && typeof data === "object" && "policies" in data) {
      return asArray((data as { policies: WorkspaceGovernancePolicy[] }).policies).map((row) =>
        normalizeWorkspaceGovernancePolicy(row as WorkspaceGovernancePolicy),
      );
    }
    return asArray(data as unknown).map((row) =>
      normalizeWorkspaceGovernancePolicy(row as WorkspaceGovernancePolicy),
    );
  },

  async updateWorkspaceGovernancePolicy(
    code: string,
    body: {
      workspaceId: string;
      isEnabled: boolean;
      params?: Record<string, unknown>;
    },
  ): Promise<WorkspaceGovernancePolicy> {
    const payload = await request.put<Envelope<WorkspaceGovernancePolicy>>(
      `/admin/governance/policies/${encodeURIComponent(code)}`,
      body,
    );
    return normalizeWorkspaceGovernancePolicy(unwrapData(payload));
  },

  async listAuditEvents(params?: {
    page?: number;
    limit?: number;
    action?: string;
    userId?: string;
    actorId?: string;
    dateRange?: string;
    eventCategory?: string;
    search?: string;
  }): Promise<{ data: AdminAuditEvent[]; meta: PageMeta | null }> {
    const payload = await request.get<any>(`/admin/audit${buildQuery(params || {})}`);
    const rawRows = asArray<any>(unwrapData(payload));
    const data: AdminAuditEvent[] = rawRows.map((row) => ({
      id: String(row.id ?? ""),
      createdAt: String(row.createdAt ?? ""),
      actorUserId: row.actorUserId ?? null,
      actorName: row.actorName ?? null,
      action: String(row.action ?? ""),
      entityType: String(row.entityType ?? ""),
      entityId: String(row.entityId ?? ""),
      description:
        typeof row.description === "string" && row.description.trim()
          ? row.description.trim()
          : `${String(row.action ?? "event")} on ${String(row.entityType ?? "record")}`,
    }));
    return { data, meta: unwrapMeta(payload) };
  },

  // ── MVP-3A: Workspace Member Management ──────────────────────────
  // Calls AdminWorkspaceMembersController endpoints at
  // /workspaces/:workspaceId/members (NOT /admin/workspaces/...).
  // Admin-only via @RequireOrgRole(ADMIN) guard on backend.
  // Role values are simplified: 'owner' | 'member' | 'viewer'.

  async listWorkspaceMembers(workspaceId: string): Promise<WorkspaceMemberRow[]> {
    const payload = await request.get<any>(`/workspaces/${workspaceId}/members`);
    const raw = payload && typeof payload === "object" ? payload : {};
    const list = Array.isArray(raw.members)
      ? raw.members
      : Array.isArray(raw.data)
        ? raw.data
        : Array.isArray(raw)
          ? raw
          : asArray(unwrapData(raw));
    return list.map((m: unknown) => mapWorkspaceMemberRow(m));
  },

  async addWorkspaceMember(
    workspaceId: string,
    input: { userId: string; role: "owner" | "member" | "viewer" },
  ): Promise<{ id: string }> {
    const payload = await request.post<Envelope<{ id: string }>>(
      `/workspaces/${workspaceId}/members`,
      { userId: input.userId, role: `workspace_${input.role}` },
    );
    return unwrapData(payload);
  },

  async updateWorkspaceMemberRole(
    workspaceId: string,
    userId: string,
    role: "owner" | "member" | "viewer",
  ): Promise<{ success: boolean }> {
    const payload = await request.patch<Envelope<{ success: boolean }>>(
      `/workspaces/${workspaceId}/members/${userId}`,
      { workspaceRole: workspaceRoleWire(role) },
    );
    return unwrapData(payload);
  },

  async removeWorkspaceMember(
    workspaceId: string,
    userId: string,
  ): Promise<{ success: boolean }> {
    const payload = await request.delete<Envelope<{ success: boolean }>>(
      `/workspaces/${workspaceId}/members/${userId}`,
    );
    return unwrapData(payload);
  },

  async listOrgMembersForAssignment(): Promise<OrgMemberOption[]> {
    const result = await this.listUsers({ limit: 200 });
    return result.data.map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      role: u.role,
      status: u.status,
    }));
  },

  async getTrashItems(params: {
    page?: number;
    limit?: number;
    type?: string;
    search?: string;
  }): Promise<{ data: AdministrationTrashItem[]; meta: PageMeta | null }> {
    const query = buildQuery({
      page: params.page,
      limit: params.limit,
      type: params.type,
      search: params.search,
    });
    const payload = await request.get<unknown>(`/admin/trash${query}`);
    return {
      data: asArray<AdministrationTrashItem>(unwrapData(payload)),
      meta: unwrapMeta(payload),
    };
  },

  async restoreTrashItem(kind: string, id: string): Promise<{ restored: boolean; type: string; id: string }> {
    const payload = await request.post<Envelope<{ restored: boolean; type: string; id: string }>>(
      `/admin/trash/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/restore`,
    );
    return unwrapData(payload);
  },

  async permanentlyDeleteTrashItem(kind: string, id: string): Promise<{ deleted: boolean; type: string; id: string }> {
    const payload = await request.delete<Envelope<{ deleted: boolean; type: string; id: string }>>(
      `/admin/trash/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`,
    );
    return unwrapData(payload);
  },

  async clearAllTrash(): Promise<{
    cleared: boolean;
    counts: { tasks: number; projects: number; workspaces: number };
  }> {
    const payload = await request.delete<
      Envelope<{ cleared: boolean; counts: { tasks: number; projects: number; workspaces: number } }>
    >("/admin/trash");
    return unwrapData(payload);
  },

  async getRetentionPolicy(): Promise<{ automaticPurge: boolean; defaultRetentionDays: number }> {
    type Retention = { automaticPurge?: boolean; defaultRetentionDays?: number };
    const payload = await request.get<Envelope<Retention> | Retention>("/admin/trash/retention-policy");
    const data = unwrapData(payload);
    return {
      automaticPurge: data?.automaticPurge === true,
      defaultRetentionDays:
        typeof data?.defaultRetentionDays === "number" ? data.defaultRetentionDays : 0,
    };
  },

  /**
   * GET /admin/rbac/migration-summary — returns null when the endpoint is not deployed (404).
   */
  async getRbacMigrationSummary(): Promise<RbacMigrationSummary | null> {
    try {
      const payload = await request.get<Envelope<RbacMigrationSummary> | RbacMigrationSummary>(
        "/v1/admin/rbac/migration-summary",
      );
      const inner = unwrapData(payload as Envelope<RbacMigrationSummary>);
      if (!inner || typeof inner !== "object") return null;
      const migratedUserCount =
        typeof (inner as RbacMigrationSummary).migratedUserCount === "number"
          ? (inner as RbacMigrationSummary).migratedUserCount
          : 0;
      const pmMappingExceptions = Array.isArray((inner as RbacMigrationSummary).pmMappingExceptions)
        ? (inner as RbacMigrationSummary).pmMappingExceptions
        : [];
      return {
        migratedUserCount,
        pmMappingExceptions,
        generatedAt:
          typeof (inner as RbacMigrationSummary).generatedAt === "string"
            ? (inner as RbacMigrationSummary).generatedAt
            : undefined,
      };
    } catch {
      return null;
    }
  },
};
